import React, { useMemo } from "react";
import styled from "styled-components";
import { useNavigate } from "react-router-dom";
import { CardContent, SkeletonLoader, ButtonGhostCompact, colors2 } from "oolib";
import { UI_BODY_SEMIBOLD_SM_DF, UI_CAPTION } from "oolib/dist/v2/components/Typo2";
import { FileText } from "@phosphor-icons/react";
import { parseSpecialConfigSyntax } from "@okf/ootils";
import { useGetQueryData } from "@/utils/react-query-hooks/general";
import { useGetAllReports } from "@/components/analysisModules/reports/ReportLanding/queries";
import { DashboardWidgetShell } from "../comps/DashboardWidgetShell";

/**
 * The featured reports as a scrolling strip — the ones someone has marked
 * in the report's own Options menu, most recently edited first. The same
 * card the Reports listing draws, so a report looks the same in both
 * places. Nothing to show until a report is featured.
 *
 * A card opens the report the way the listing opens it: its author edits,
 * everyone else views.
 */
const TOP_N = 12;
const Strip = styled.div`
  display: flex;
  gap: 1.6rem;
  overflow-x: auto;
  padding-top: 12px;
  padding-bottom: 8px;
  scrollbar-width: thin;
`;
const Slot = styled.div`
  flex: 0 0 26rem;
  max-width: 26rem;
`;
const Foot = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: -4px;
`;

export const FeaturedReports = () => {
  const navigate = useNavigate();
  const { data, status } = useGetAllReports();
  const { SELF_MANAGED_BASE_CONFIGS } = useGetQueryData("platformConfigs") || {};

  const reports = useMemo(
    () =>
      (data || [])
        .filter((d) => d.featured)
        .sort((a, b) => new Date(b.dateLastEdited || 0) - new Date(a.dateLastEdited || 0))
        .slice(0, TOP_N),
    [data]
  );
  const openReport = (d) =>
    navigate(
      parseSpecialConfigSyntax({
        config: SELF_MANAGED_BASE_CONFIGS?.reports?.linkToViewReport || `/reports/view/${d._id}`,
        content: d,
      })
    );

  return (
    <DashboardWidgetShell
      titleJSX={
        <div style={{ display: "flex", alignItems: "center", gap: "0.6rem", minWidth: 0 }}>
          <FileText size={16} color={colors2.grey80} weight="bold" />
          <UI_BODY_SEMIBOLD_SM_DF>Featured reports</UI_BODY_SEMIBOLD_SM_DF>
        </div>
      }
      style={{ gap: 0 }}
    >
      <Strip>
        {status === "loading" &&
          [0, 1, 2, 3].map((i) => (
            <Slot key={i}>
              <SkeletonLoader style={{ height: "17rem", borderRadius: "1.4rem" }} />
            </Slot>
          ))}
        {status !== "loading" && reports.length === 0 && (
          <UI_CAPTION style={{ color: colors2.grey60, padding: "12px 0" }}>
            No featured reports yet — open a report and turn on “Feature on the dashboard”.
          </UI_CAPTION>
        )}
        {reports.map((d) => (
          <Slot key={d._id}>
            <CardContent
              data={d}
              onClick={() => openReport(d)}
              config={{
                title: "title",
                desc: "subtitle",
                image: "path.doesnt.exist",
                metaBlock: [
                  { key: "name", valuePath: "author.name" },
                  { key: "date", valuePath: "dateLastEdited" },
                ],
              }}
            />
          </Slot>
        ))}
      </Strip>
      {reports.length > 0 && (
        <Foot>
          <ButtonGhostCompact
            S
            value={`All ${data?.length || 0} reports`}
            iconAfter="ArrowRight"
            onClick={() => navigate(SELF_MANAGED_BASE_CONFIGS?.reports?.linkToReportsListing || "/reports")}
          />
        </Foot>
      )}
    </DashboardWidgetShell>
  );
};
